import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Flame, MessagesSquare, Radio, Sun } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { MotionCard } from '@/components/motion/MotionCard';
import { DeltaBadge } from '@/components/charts/DeltaBadge';
import { EmptyState } from '@/components/charts/EmptyState';
import { AnimatedNumber } from '@/components/motion/AnimatedNumber';
import { MoversList } from '@/components/charts/MoversList';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useDayMetric } from '@/hooks/useDayMetric';
import { useLiveToday } from '@/hooks/useLiveToday';
import { useOutletCapabilities } from '@/hooks/useOutlets';
import { useOutletDayRanking } from '@/hooks/useOutletDayRanking';
import { pickDrops, pickSpikes } from '@/hooks/useOutletRanking';
import type { MetricSource } from '@/hooks/useMetric';
import { fmtISTDate, fmtRelative, formatINRCompact, formatNumberCompact } from '@/lib/format';

type Caps = ReturnType<typeof useOutletCapabilities>;

interface TileSpec {
  label: string;
  source: MetricSource;
  format: (n: number) => string;
  gate?: 'aggregator' | 'qsr' | 'dineIn';
}

const TILES: TileSpec[] = [
  { label: 'Revenue', source: 'net_sales', format: formatINRCompact },
  { label: 'Orders', source: 'orders', format: formatNumberCompact },
  { label: 'Covers', source: 'covers', format: formatNumberCompact, gate: 'dineIn' },
  { label: 'Avg ticket', source: 'avg_ticket', format: formatINRCompact },
];

export function TodayPage() {
  const { outletId } = useDashboard();
  const caps = useOutletCapabilities(outletId);

  return (
    <Tabs defaultValue="today" className="space-y-6">
      <TabsList>
        <TabsTrigger value="today" className="flex items-center gap-1.5">
          <Radio className="h-3.5 w-3.5 text-emerald-500" />
          Today
        </TabsTrigger>
        <TabsTrigger value="yesterday" className="flex items-center gap-1.5">
          <Sun className="h-3.5 w-3.5 text-amber-500" />
          Yesterday
        </TabsTrigger>
      </TabsList>

      <TabsContent value="today" className="space-y-8">
        <TodaySoFar outletId={outletId} caps={caps} />
      </TabsContent>

      <TabsContent value="yesterday" className="space-y-8">
        <YesterdayView outletId={outletId} caps={caps} />
      </TabsContent>
    </Tabs>
  );
}

function YesterdayView({ outletId, caps }: { outletId: string | 'all'; caps: Caps }) {
  const dayRanking = useOutletDayRanking('net_sales');
  const drops = dayRanking.data ? pickDrops(dayRanking.data.rankings, 3) : [];
  const spikes = dayRanking.data ? pickSpikes(dayRanking.data.rankings, 3) : [];
  const fires = dayRanking.data
    ? dayRanking.data.rankings.filter(
        (r) => r.metric.comparable && r.metric.previous > 0 && r.metric.deltaPct <= -15,
      )
    : [];

  return (
    <>
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-amber-500" />
            How yesterday went
          </span>
        }
        description="Yesterday's headline metrics with same-day-last-week and same-day-last-month comparisons."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {TILES.filter((t) => !t.gate || caps[t.gate]).map((t, i) => (
          <DayTile key={t.label} spec={t} index={i} outletId={outletId} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <MotionCard>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowDownRight className="h-4 w-4 text-negative" />
                Top 3 drops
                <span className="ml-1 text-2xs font-normal text-muted-foreground">
                  vs same day last week
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-3">
              <MoversList
                rankings={drops}
                loading={dayRanking.isLoading}
                tone="drop"
                format={formatINRCompact}
                emptyTitle="No drops"
                emptyDescription="Every outlet held above its same-day-last-week baseline."
              />
            </CardContent>
          </Card>
        </MotionCard>
        <MotionCard delay={0.05}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowUpRight className="h-4 w-4 text-positive" />
                Top 3 spikes
                <span className="ml-1 text-2xs font-normal text-muted-foreground">
                  vs same day last week
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-3">
              <MoversList
                rankings={spikes}
                loading={dayRanking.isLoading}
                tone="spike"
                format={formatINRCompact}
                emptyTitle="No spikes"
                emptyDescription="No outlet beat its same-day-last-week baseline."
              />
            </CardContent>
          </Card>
        </MotionCard>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <MotionCard>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <MessagesSquare className="h-4 w-4" /> Unreplied reviews
              </CardTitle>
              <Badge variant="muted">—</Badge>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Wires when the `reviews` table lands (Phase 2 schema, Phase 3 module).
            </CardContent>
          </Card>
        </MotionCard>
        <MotionCard delay={0.05}>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <Flame className="h-4 w-4 text-amber-500" /> Active fires
                <span className="ml-1 text-2xs font-normal text-muted-foreground">
                  Δ≤ -15% vs same day last week
                </span>
              </CardTitle>
              <Badge variant={fires.length > 0 ? 'negative' : 'muted'}>{fires.length}</Badge>
            </CardHeader>
            <CardContent className="px-5 pb-3">
              <MoversList
                rankings={fires}
                loading={dayRanking.isLoading}
                tone="drop"
                format={formatINRCompact}
                emptyTitle="No active fires"
                emptyDescription="No outlet is more than 15% below its same-day-last-week baseline."
              />
            </CardContent>
          </Card>
        </MotionCard>
      </div>
    </>
  );
}

interface LiveTileSpec {
  label: string;
  pick: (d: { net_sales: number; orders: number; covers: number; avg_ticket: number }) => number;
  format: (n: number) => string;
  gate?: 'aggregator' | 'qsr' | 'dineIn';
}

const LIVE_TILES: LiveTileSpec[] = [
  { label: 'Revenue', pick: (d) => d.net_sales, format: formatINRCompact },
  { label: 'Orders', pick: (d) => d.orders, format: formatNumberCompact },
  { label: 'Covers', pick: (d) => d.covers, format: formatNumberCompact, gate: 'dineIn' },
  { label: 'Avg ticket', pick: (d) => d.avg_ticket, format: formatINRCompact },
];

function TodaySoFar({ outletId, caps }: { outletId: string | 'all'; caps: Caps }) {
  const { data, isLoading, isError } = useLiveToday(outletId);
  const tiles = LIVE_TILES.filter((t) => !t.gate || caps[t.gate]);

  const freshness = data?.syncedAt ? `Updated ${fmtRelative(data.syncedAt)}` : 'Awaiting first sync';

  return (
    <div className="space-y-4">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-emerald-500" />
            Today so far
            <span className="relative flex h-2 w-2" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
          </span>
        }
        description={`Live running totals from Petpooja orders, today (IST). ${freshness}.`}
      />

      {isError ? (
        <Card>
          <CardContent className="py-6">
            <EmptyState
              title="Live sync unavailable"
              description="Couldn't reach outlet_sales_live. It populates once the petpooja-sales-fetch function runs."
            />
          </CardContent>
        </Card>
      ) : !isLoading && data && !data.hasData ? (
        <Card>
          <CardContent className="py-6">
            <EmptyState
              title="No orders synced yet today"
              description="Waiting for the first Petpooja live sync of the day (runs every ~30 min)."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((t, i) => (
            <LiveTile
              key={t.label}
              label={t.label}
              index={i}
              loading={isLoading || !data}
              value={data ? t.pick(data) : 0}
              format={t.format}
              syncedAt={data?.syncedAt ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LiveTile({
  label,
  index,
  loading,
  value,
  format,
  syncedAt,
}: {
  label: string;
  index: number;
  loading: boolean;
  value: number;
  format: (n: number) => string;
  syncedAt: string | null;
}) {
  return (
    <MotionCard delay={index * 0.04}>
      <Card>
        <CardHeader>
          <CardTitle>{label}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-9 w-32" />
          ) : (
            <div className="font-semibold tabular-nums text-3xl tracking-tight">
              <AnimatedNumber value={value} format={format} />
            </div>
          )}
          <p className="mt-4 border-t border-border/60 pt-3 text-2xs text-muted-foreground">
            {syncedAt ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Live · {fmtRelative(syncedAt)}
              </span>
            ) : (
              'Live · awaiting sync'
            )}
          </p>
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function DayTile({
  spec,
  index,
  outletId,
}: {
  spec: TileSpec;
  index: number;
  outletId: string | 'all';
}) {
  const { data, isLoading, isError } = useDayMetric(outletId, spec.source);

  if (isLoading) {
    return (
      <MotionCard delay={index * 0.04}>
        <Card>
          <CardHeader>
            <CardTitle>{spec.label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-16 w-full" />
          </CardContent>
        </Card>
      </MotionCard>
    );
  }

  if (isError || !data) {
    return (
      <MotionCard delay={index * 0.04}>
        <Card>
          <CardHeader>
            <CardTitle>{spec.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <EmptyState title="No data" description="Sync hasn't landed yesterday's row yet." />
          </CardContent>
        </Card>
      </MotionCard>
    );
  }

  return (
    <MotionCard delay={index * 0.04}>
      <Card>
        <CardHeader>
          <CardTitle>{spec.label}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="font-semibold tabular-nums text-3xl tracking-tight">
            <AnimatedNumber value={data.yesterday} format={spec.format} />
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-border/60 pt-3 text-2xs">
            <ComparisonCell
              label="vs same day last week"
              metric={data.vsLastWeek}
              wasValue={spec.format(data.vsLastWeek.previous)}
              wasDate={fmtISTDate(data.rangeLastWeek.start)}
            />
            <ComparisonCell
              label="vs same day last month"
              metric={data.vsLastMonth}
              wasValue={spec.format(data.vsLastMonth.previous)}
              wasDate={fmtISTDate(data.rangeLastMonth.start)}
            />
          </dl>
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function ComparisonCell({
  label,
  metric,
  wasValue,
  wasDate,
}: {
  label: string;
  metric: { deltaPct: number };
  wasValue: string;
  wasDate: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>
        <DeltaBadge deltaPct={metric.deltaPct} size="md" />
      </dd>
      <dd className="font-mono text-muted-foreground">
        was {wasValue} · {wasDate}
      </dd>
    </div>
  );
}
